import { useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeHighlight from "rehype-highlight";
import { Card, Text, Flex, Badge, Callout } from "@radix-ui/themes";
import { SchemaPreviewProps } from "./types";
import { validateSchema } from "./utils";
import { ExclamationTriangleIcon } from "@radix-ui/react-icons";

const rehypeHighlightOptions = {
  detect: true,
  ignoreMissing: true,
};

export function SchemaPreview({ schema }: SchemaPreviewProps) {
  const validation = useMemo(() => {
    return validateSchema(schema);
  }, [schema]);

  const markdownCodeBlock = useMemo(() => {
    return `\`\`\`json\n${JSON.stringify(schema, null, 2)}\n\`\`\``;
  }, [schema]);

  return (
    <Card className="schema-preview" style={{ minWidth: 400, maxWidth: 500 }}>
      <Flex direction="column" gap="3">
        <Flex justify="between" align="center">
          <Text size="3" weight="bold">
            Schema Preview
          </Text>
          <Flex gap="2" align="center">
            <Badge
              size="2"
              color={validation.valid ? "green" : "red"}
              variant="surface"
            >
              {validation.valid ? "Valid" : "Invalid"}
            </Badge>
          </Flex>
        </Flex>

        {!validation.valid && (
          <Callout.Root color="red" variant="soft">
            <Callout.Icon>
              <ExclamationTriangleIcon />
            </Callout.Icon>
            <Callout.Text>
              <Text size="2" weight="medium" mb="2">
                Validation Errors:
              </Text>
              <ul>
                {validation.errors.map((error, index) => (
                  <li key={index}>
                    <Text size="1" color="red">
                      {error}
                    </Text>
                  </li>
                ))}
              </ul>
            </Callout.Text>
          </Callout.Root>
        )}

        <div className="schema-content">
          <div className="prose prose-sm max-w-none dark:prose-invert [&>pre]:m-0 [&>pre]:p-0 [&>pre>code]:!p-4">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[[rehypeHighlight, rehypeHighlightOptions]]}
            >
              {markdownCodeBlock}
            </ReactMarkdown>
          </div>
        </div>
      </Flex>
    </Card>
  );
}
